import React, { useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import GenderIcon from '../common/GenderIcon';
import LocalizedLink from '../common/LocalizedLink';
import { useLanguage } from '../../contexts/LanguageContext';

const { FiCalendar, FiClock, FiShield, FiMapPin, FiShare2, FiChevronRight } = FiIcons;

// ✅ 移到組件外部，避免每次 render 重建
const PET_TYPE_MAP = {
  dog:    { emoji: '🐕', bgGradient: 'from-orange-100 to-amber-50' },
  cat:    { emoji: '🐱', bgGradient: 'from-blue-100 to-indigo-50' },
  bird:   { emoji: '🦜', bgGradient: 'from-green-100 to-emerald-50' },
  fish:   { emoji: '🐠', bgGradient: 'from-cyan-100 to-blue-50' },
  rabbit: { emoji: '🐰', bgGradient: 'from-pink-100 to-rose-50' },
};
const DEFAULT_PET_TYPE = { emoji: '🐾', bgGradient: 'from-gray-100 to-slate-50' };

const PetCard = ({ pet, onShare }) => {
  const { t } = useLanguage();

  if (!pet) return null;

  const { petEmoji, bgGradient } = useMemo(() => {
    const normalizedType = pet.type ? String(pet.type).toLowerCase().trim() : '';
    return PET_TYPE_MAP[normalizedType] || DEFAULT_PET_TYPE;
  }, [pet.type]);

  const ageDisplay = useMemo(() => {
    if (pet._computed?.age) return pet._computed.age;
    if (pet.age) return `${pet.age} ${t('dashboard.yearsOld')}`;
    return '';
  }, [pet._computed?.age, pet.age, t]);

  // ✅ 修正：handleShare 應用 useCallback 而非 useMemo（函數用 useCallback）
  const handleShare = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    onShare && onShare(pet);
  }, [onShare, pet]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      whileHover={{ y: -5, boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}
      className="relative w-full h-[400px] rounded-3xl overflow-hidden shadow-lg group bg-white"
    >
      {/* Background */}
      <div className={`absolute inset-0 w-full h-full bg-gradient-to-br ${bgGradient} transition-transform duration-700 group-hover:scale-105`}>
        {pet.photo_url ? (
          <img
            src={pet.photo_url}
            alt={pet.name}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div className={`w-full h-full flex items-center justify-center ${pet.photo_url ? 'hidden' : 'flex'}`}>
          <span className="text-9xl opacity-50 grayscale-[0.2]">{petEmoji}</span>
        </div>
      </div>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent/10 pointer-events-none" />

      {/* Share Button */}
      <div className="absolute top-4 right-4 z-10">
        <button
          onClick={handleShare}
          className="p-3 bg-white/20 backdrop-blur-md rounded-full text-white hover:bg-white hover:text-indigo-600 transition-all duration-300 shadow-lg transform hover:scale-110"
          title={t('dashboard.shareNow')}
        >
          <SafeIcon icon={FiShare2} className="w-5 h-5" />
        </button>
      </div>

      {/* Main Content */}
      <LocalizedLink
        to={`/pet/${pet.id}`}
        className="absolute inset-0 flex flex-col justify-end p-5 z-1 text-white"
      >
        {/* Header */}
        <div className="mb-4 transform transition-transform duration-300 group-hover:-translate-y-2">
          <div className="flex items-center space-x-3 mb-2">
            <h3 className="text-3xl font-bold tracking-tight shadow-black drop-shadow-md">
              {pet.name || t('dashboard.unnamedPet')}
            </h3>
            <div className="shadow-lg rounded-full">
              <GenderIcon gender={pet.gender} petType={pet.type} size="md" className="border-2 border-white/30" />
            </div>
          </div>
          <p className="text-white/80 font-medium text-sm flex items-center">
            <span>{pet.breed || t('dashboard.unknownBreed')}</span>
            {ageDisplay && (
              <>
                <span className="mx-2">•</span>
                <span>{ageDisplay}</span>
              </>
            )}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-2 bg-white/10 backdrop-blur-md rounded-xl p-3 border border-white/10 transition-all duration-300 group-hover:bg-white/20">
          <div className="flex items-start space-x-2 p-1.5">
            <div className="p-1.5 bg-blue-500/30 rounded-lg text-blue-200 mt-0.5">
              <SafeIcon icon={FiCalendar} className="w-3.5 h-3.5" />
            </div>
            <div className="min-w-0 overflow-hidden">
              <p className="text-[10px] text-blue-200 uppercase tracking-wider mb-0.5">{t('dashboard.nextFeeding')}</p>
              <p className="text-xs font-semibold truncate">{pet._computed?.nextFeeding || t('dashboard.notSet')}</p>
            </div>
          </div>

          <div className="flex items-start space-x-2 p-1.5">
            <div className="p-1.5 bg-purple-500/30 rounded-lg text-purple-200 mt-0.5">
              <SafeIcon icon={FiClock} className="w-3.5 h-3.5" />
            </div>
            <div className="min-w-0 overflow-hidden">
              <p className="text-[10px] text-purple-200 uppercase tracking-wider mb-0.5">{t('dashboard.nextMedication')}</p>
              <p className="text-xs font-semibold truncate">{pet._computed?.nextMedication || t('dashboard.notSet')}</p>
            </div>
          </div>

          <div className="flex items-center space-x-2 p-1.5">
            <div className="p-1.5 bg-orange-500/30 rounded-lg text-orange-200">
              <SafeIcon icon={FiMapPin} className="w-3.5 h-3.5" />
            </div>
            <div className="min-w-0 overflow-hidden">
              <p className="text-[10px] text-orange-200 uppercase tracking-wider">{t('dashboard.location')}</p>
              <p className="text-xs font-semibold truncate">{pet.current_location || t('dashboard.home')}</p>
            </div>
          </div>

          <div className="flex items-center space-x-2 p-1.5">
            <div className="p-1.5 bg-green-500/30 rounded-lg text-green-200">
              <SafeIcon icon={FiShield} className="w-3.5 h-3.5" />
            </div>
            <div className="min-w-0 overflow-hidden">
              <p className="text-[10px] text-green-200 uppercase tracking-wider">{t('dashboard.nextVetVisit')}</p>
              <p className="text-xs font-semibold truncate">{pet.next_vaccine_date || t('dashboard.notSet')}</p>
            </div>
          </div>
        </div>

        {/* View Details Indicator */}
        <div className="absolute bottom-5 right-5 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="bg-white text-indigo-600 p-2 rounded-full shadow-lg">
            <SafeIcon icon={FiChevronRight} className="w-5 h-5" />
          </div>
        </div>
      </LocalizedLink>
    </motion.div>
  );
};

export default React.memo(PetCard);